import { BaseApiClient } from './base-client'
import {
  type ApiResponse,
  type Graph,
  type GraphRecord,
  type GraphSummary,
  API_ROUTES,
  interpolateRoute,
} from '@super/shared-types'
import { withErrorHandling } from '../utils'

/**
 * Graphs for the session's own company. The body is the graph contract
 * itself: a save sends the whole graph, and the API keeps the node and edge
 * ids it was given so a call can point back at what it ran.
 */
export type SaveGraphRequest = Graph

export class GraphsApiClient extends BaseApiClient {
  async list(): Promise<GraphSummary[]> {
    const result = await withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<GraphSummary[]>>(
          API_ROUTES.GRAPHS.GET_ALL,
        )
        return response.data ?? []
      },
      {
        onApiError: 'log-and-return-null',
        onOtherError: 'log-and-return-null',
        errorContext: 'Error fetching graphs',
      },
    )
    return result ?? []
  }

  async get(id: string): Promise<GraphRecord | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<GraphRecord>>(
          interpolateRoute(API_ROUTES.GRAPHS.GET, { id }),
        )
        return response.data ?? null
      },
      { on404: 'ignore', errorContext: 'Error fetching graph' },
    )
  }

  async create(dto: SaveGraphRequest): Promise<GraphRecord | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<GraphRecord>>(
          API_ROUTES.GRAPHS.CREATE,
          { method: 'POST', body: JSON.stringify(dto) },
        )
        return response.data ?? null
      },
      { errorContext: 'Error creating graph' },
    )
  }

  async save(id: string, dto: SaveGraphRequest): Promise<GraphRecord | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<GraphRecord>>(
          interpolateRoute(API_ROUTES.GRAPHS.UPDATE, { id }),
          { method: 'PUT', body: JSON.stringify(dto) },
        )
        return response.data ?? null
      },
      { errorContext: 'Error saving graph' },
    )
  }

  async remove(id: string): Promise<boolean> {
    const result = await withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<{ deleted: boolean }>>(
          interpolateRoute(API_ROUTES.GRAPHS.DELETE, { id }),
          { method: 'DELETE' },
        )
        return response.data?.deleted === true
      },
      { errorContext: 'Error deleting graph' },
    )
    return result === true
  }
}

export const graphsApiClient = new GraphsApiClient()
